import { contentToPDF } from "@/documentos/pdf/contentToPDF";
import logoPolicia from "@/assets/policia.png";
import logoSiis from "@/assets/logo-siis-texto.png";
import { dynamicSections, personaFields } from "@/utils/sifcopValidation";

const hasValue = (value) => {
  if (value === null || value === undefined) return false;
  if (typeof value === "string") return value.trim() !== "";
  return true;
};

const escapeHtml = (value = "") =>
  String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");

const formatDateValue = (value) => {
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return value.toLocaleDateString("es-AR");
  }

  const parsedDate = new Date(value);
  if (!Number.isNaN(parsedDate.getTime())) {
    return parsedDate.toLocaleDateString("es-AR");
  }

  return value;
};

const formatTimeValue = (value) => {
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return value.toLocaleTimeString("es-AR", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
  }

  if (typeof value === "string" && /^([01]\d|2[0-3]):([0-5]\d)$/.test(value)) {
    return value;
  }

  const parsedDate = new Date(value);
  if (!Number.isNaN(parsedDate.getTime())) {
    return parsedDate.toLocaleTimeString("es-AR", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
  }

  return value;
};

const formatPdfValue = (field, value) => {
  if (!hasValue(value)) return "-";

  const safeValue = field?.type === "date" ? escapeHtml(formatDateValue(value)) : escapeHtml(value);
  return safeValue;
};

const formatActaInlineValue = (value, fallback = "-", type = "text") => {
  if (!hasValue(value)) return escapeHtml(fallback);

  let safeValue = value;
  if (type === "date") safeValue = formatDateValue(value);
  if (type === "time") safeValue = formatTimeValue(value);

  return escapeHtml(safeValue);
};

const buildDatosGeneralesActaHtml = (values = {}) => {
  const organismoInterviniente = formatActaInlineValue(values.organismoInterviniente);
  const lugar = formatActaInlineValue(values.lugar);
  const fecha = formatActaInlineValue(values.fecha, "-", "date");
  const provincia = formatActaInlineValue(values.provincia);
  const departamento = formatActaInlineValue(values.departamento);
  const dependencia = formatActaInlineValue(values.dependenciaPolicial);
  const dia = formatActaInlineValue(values.dia);
  const mes = formatActaInlineValue(values.mes);
  const anio = formatActaInlineValue(values.anio);
  const hora = formatActaInlineValue(values.hora, "-", "time");
  const funcionario = formatActaInlineValue(values.funcionarioPolicialSuscribe);
  const usuarioSifcop = formatActaInlineValue(values.usuarioSifcop);
  const nroRequerimiento = formatActaInlineValue(values.numeroRequerimientoJudicial, "S/D");

  return `
    <section>
      <p style="text-align: justify; margin-bottom: 10px;">
        Organismo Interviniente: ${organismoInterviniente}
      </p>

      <p style="text-align: justify; margin-bottom: 10px;">
        Dependencia Policial: ${dependencia}
      </p>

      <p style="text-align: justify; margin-bottom: 10px;">
        Lugar y fecha: ${lugar}, ${fecha}
      </p>

      <p style="text-align: justify; margin-bottom: 10px;">
        En el Departamento de ${departamento}, provincia de ${provincia}, siendo las ${hora} horas, del día ${dia}, del mes ${mes}, del año ${anio}, el Funcionario Policial que suscribe ${funcionario}, usuario SIFCOP ${usuarioSifcop}, en calidad de funcionario de la Policía de ${provincia}, <strong>HACE CONSTAR:</strong>
      </p>
      <p style="text-align: justify; margin-bottom: 10px;">
        Que con motivo de la consulta en el Sistema Federal de Comunicaciones Policiales (SIFCOP) del requerimiento judicial N° ${nroRequerimiento}, se constató que:
      </p>
    </section>
  `;
};

const renderInlineSummarySection = (title, fields, values) => {
  const rowsHtml = fields
    .map(
      (field) => `
        <p style="text-align: justify; margin-bottom: 10px;">
          ${escapeHtml(field.label)}: ${formatPdfValue(field, values?.[field.key])}
        </p>
      `
    )
    .join("");

  return `
    <section>
      <h2>${escapeHtml(title)}</h2>
      ${rowsHtml}
    </section>
  `;
};

const hasSectionData = (item, fields = []) => fields.some((field) => hasValue(item?.[field.key]));

const renderDynamicSection = (section, items) => {
  const safeItems = Array.isArray(items)
    ? items.filter((item) => hasSectionData(item, section.fields))
    : [];

  if (!safeItems.length) {
    return `
      <section>
        <h2>${escapeHtml(section.title)}</h2>
        <p>Sin datos registrados.</p>
      </section>
    `;
  }

  const tablesHtml = safeItems
    .map((item, index) => {
      const itemTitle = safeItems.length > 1 ? `${escapeHtml(section.itemLabel)} ${index + 1}` : "";

      const rowsHtml = section.fields
        .map(
          (field) => `
            <tr>
              <th style="width: 35%; text-align: left;">${escapeHtml(field.label)}</th>
              <td>${formatPdfValue(field, item?.[field.key])}</td>
            </tr>
          `
        )
        .join("");

      return `
        <div style="margin-bottom: 14px;">
          ${itemTitle ? `<h3 style="margin-bottom: 6px;">${itemTitle}</h3>` : ""}
          <table>
            <tbody>${rowsHtml}</tbody>
          </table>
        </div>
      `;
    })
    .join("");

  return `
    <section>
      <h2>${escapeHtml(section.title)}</h2>
      ${tablesHtml}
    </section>
  `;
};

export const buildSifcopPdfHtml = (formData = {}) => {
  const datosGeneralesHtml = buildDatosGeneralesActaHtml(formData.datosGeneralesActa || {});
  const personaHtml = renderInlineSummarySection(
    "• DATOS DE LA PERSONA REGISTRADA EN EL SIFCOP",
    personaFields,
    formData.personaRegistrada || {}
  );
  const dynamicSectionsHtml = dynamicSections
    .map((section) => renderDynamicSection(section, formData?.[section.key]))
    .join("");

  return `
    <section>
      <h3 style="text-align: center; margin-top: 0;">ACTA PARA LA ACTUALIZACIÓN DE MEDIDAS JUDICIALES EN LA BASE DE DATOS DEL SISTEMA FEDERAL DE COMUNICACIONES POLICIALES - SIFCOP</h3>
      ${datosGeneralesHtml}
      ${personaHtml}
      <h2>• DATOS DE VEHÍCULOS (A) / ARMAS (B) / ELEMENTOS (C) REGISTRADO EN EL SIFCOP</h2>
      ${dynamicSectionsHtml}
    </section>
  `;
};

export const generateSifcopPdf = async ({
  formData = {},
  outputFileName = `sifcop-${Date.now()}.pdf`,
  previewWindow = null,
} = {}) => {
  const pdf = await contentToPDF({
    html: buildSifcopPdfHtml(formData),
    outputFileName,
    autoSave: false,
    logos: {
      headers: [logoSiis, logoPolicia],
    },
  });

  const pdfUrl = URL.createObjectURL(pdf.output("blob"));

  if (previewWindow && !previewWindow.closed) {
    previewWindow.postMessage(
      {
        type: "sifcop-pdf-ready",
        pdfUrl,
        title: outputFileName,
      },
      window.location.origin
    );
    previewWindow.focus?.();
    return pdf;
  }

  const newTab = window.open(pdfUrl, "_blank");

  if (!newTab) {
    window.location.assign(pdfUrl);
  }

  return pdf;
};
